import React, { useState, useEffect } from 'react';
import { FaDice, FaFutbol, FaBroadcastTower, FaCog, FaToggleOn, FaToggleOff, FaEdit, FaTrash, FaPlus, FaSpinner, FaTrophy, FaSave, FaChartBar, FaCoins } from 'react-icons/fa';
import { adminApiService } from '../../services/adminApiService';
import { apiService } from '../../services/apiService';
import { formatAmount } from '../../utils/currency';
import { toast } from 'react-toastify';
import './AdminComponents.css';

type GameProvider = {
  id: number;
  name: string;
  enabled: boolean;
  apiKey: string;
  apiUrl: string;
  gameCount: number;
  category: 'slots' | 'table' | 'live' | 'sports';
};

type Game = {
  id: number;
  name: string;
  provider: string;
  category: string;
  rtp: string;
  volatility: string;
  minBet: number;
  maxBet: number;
  enabled: boolean;
};

interface TetrisConfig {
  id: number;
  isEnabled: boolean;
  minStake: number;
  maxStake: number;
  easyTargetScore: number;
  mediumTargetScore: number;
  hardTargetScore: number;
  easyMultiplier: number;
  mediumMultiplier: number;
  hardMultiplier: number;
}

interface TetrisSession {
  id: number;
  username: string;
  difficulty: string;
  stake: number;
  targetScore: number;
  actualScore: number | null;
  potentialWin: number;
  actualWin: number;
  status: string;
  startedAt: string;
  completedAt: string | null;
}

const IGamingManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'providers' | 'games' | 'sports' | 'settings' | 'tetris'>('providers');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<GameProvider[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [sportsConfig, setSportsConfig] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<GameProvider | null>(null);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);

  // Tetris state
  const [tetrisConfig, setTetrisConfig] = useState<TetrisConfig | null>(null);
  const [tetrisSessions, setTetrisSessions] = useState<TetrisSession[]>([]);
  const [editedTetrisConfig, setEditedTetrisConfig] = useState<TetrisConfig | null>(null);
  const [savingTetris, setSavingTetris] = useState(false);

  // Load data based on active tab
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('adminToken');
        
        if (activeTab === 'providers') {
          const response = await adminApiService.getIGamingProviders(0, 100, token || undefined);
          if (response.success && response.data) {
            setProviders(response.data.content || []);
          } else {
            setError(response.error || 'Failed to load providers');
          }
        } else if (activeTab === 'games') {
          const response = await adminApiService.getIGamingGames(0, 100, undefined, undefined, token || undefined);
          if (response.success && response.data) {
            setGames(response.data.content || []);
          } else {
            setError(response.error || 'Failed to load games');
          }
        } else if (activeTab === 'sports') {
          const response = await adminApiService.getIGamingSportsConfig(token || undefined);
          if (response.success && response.data) {
            setSportsConfig(response.data);
          } else {
            setError(response.error || 'Failed to load sports config');
          }
        } else if (activeTab === 'settings') {
          const response = await adminApiService.getIGamingSettings(token || undefined);
          if (response.success && response.data) {
            setSettings(response.data);
          } else {
            setError(response.error || 'Failed to load settings');
          }
        } else if (activeTab === 'tetris') {
          await loadTetrisData();
        }
      } catch (err: any) {
        console.error('Failed to load iGaming data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeTab]);

  const loadTetrisData = async () => {
    try {
      const [configRes, sessionsRes] = await Promise.all([
        apiService.get('/admin/igaming/tetris/config'),
        apiService.get('/admin/igaming/tetris/sessions')
      ]);

      if (configRes.success) {
        setTetrisConfig(configRes.data);
        setEditedTetrisConfig(configRes.data);
      }
      if (sessionsRes.success) {
        setTetrisSessions(sessionsRes.data);
      }
    } catch (err) {
      console.error('Failed to load Tetris data:', err);
      toast.error('Failed to load Tetris game data');
    }
  };

  const toggleTetrisGame = async () => {
    if (!tetrisConfig) return;
    
    setSavingTetris(true);
    try {
      const response = await apiService.put(`/admin/igaming/tetris/toggle?enabled=${!tetrisConfig.isEnabled}`);
      if (response.success) {
        setTetrisConfig(response.data);
        setEditedTetrisConfig(response.data);
        toast.success(`Tetris game ${!tetrisConfig.isEnabled ? 'enabled' : 'disabled'}`);
      }
    } catch (err) {
      toast.error('Failed to toggle Tetris game');
    } finally {
      setSavingTetris(false);
    }
  };

  const saveTetrisConfig = async () => {
    if (!editedTetrisConfig) return;
    
    setSavingTetris(true);
    try {
      const response = await apiService.put('/admin/igaming/tetris/config', editedTetrisConfig);
      if (response.success) {
        setTetrisConfig(response.data);
        setEditedTetrisConfig(response.data);
        toast.success('Configuration saved successfully');
      }
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingTetris(false);
    }
  };

  const tabs = [
    { id: 'providers' as const, label: 'Game Providers', icon: <FaDice style={{ fontSize: 18 }} /> },
    { id: 'games' as const, label: 'Games', icon: <FaDice style={{ fontSize: 18 }} /> },
    { id: 'sports' as const, label: 'Sports Betting', icon: <FaFutbol style={{ fontSize: 18 }} /> },
    { id: 'settings' as const, label: 'Settings', icon: <FaCog style={{ fontSize: 18 }} /> },
    { id: 'tetris' as const, label: 'Tetris Game', icon: <FaTrophy style={{ fontSize: 18 }} /> }
  ];

  const toggleProvider = async (providerId: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await adminApiService.toggleIGamingProvider(providerId, token || undefined);
      
      if (response.success && response.data) {
        setProviders(providers.map(p => 
          p.id === providerId ? { ...p, enabled: response.data.enabled } : p
        ));
      } else {
        setError(response.error || 'Failed to toggle provider');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle provider');
    } finally {
      setLoading(false);
    }
  };

  const toggleGame = async (gameId: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await adminApiService.toggleIGamingGame(gameId, token || undefined);
      
      if (response.success && response.data) {
        setGames(games.map(g => 
          g.id === gameId ? { ...g, enabled: response.data.enabled } : g
        ));
      } else {
        setError(response.error || 'Failed to toggle game');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle game');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async (updatedSettings: any) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await adminApiService.updateIGamingSettings(updatedSettings, token || undefined);
      
      if (response.success) {
        setSettings(updatedSettings);
        setError(null);
      } else {
        setError(response.error || 'Failed to update settings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  // Calculate Tetris statistics
  const tetrisStats = {
    totalSessions: tetrisSessions.length,
    wonSessions: tetrisSessions.filter(s => s.status === 'WON').length,
    lostSessions: tetrisSessions.filter(s => s.status === 'LOST').length,
    totalStaked: tetrisSessions.reduce((sum, s) => sum + s.stake, 0),
    totalWon: tetrisSessions.filter(s => s.status === 'WON').reduce((sum, s) => sum + s.actualWin, 0),
  };
  const tetrisProfit = tetrisStats.totalStaked - tetrisStats.totalWon;
  const tetrisWinRate = tetrisStats.totalSessions > 0 
    ? (tetrisStats.wonSessions / (tetrisStats.wonSessions + tetrisStats.lostSessions)) * 100 
    : 0;

  return (
    <div className="igaming-management" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h2>iGaming Management</h2>
          <p>Manage game providers, games, and sports betting configuration</p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          padding: '16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 12,
          color: '#dc2626'
        }}>
          <p style={{ margin: 0, fontSize: 14 }}>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px'
        }}>
          <FaSpinner className="animate-spin" style={{ fontSize: 32, color: '#086afb' }} />
        </div>
      )}

      {/* Tabs */}
      <div style={{
        background: '#ffffff',
        borderRadius: 12,
        padding: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: 8,
        border: '1px solid #e5e7eb',
        overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: activeTab === tab.id ? '#086afb' : 'transparent',
              color: activeTab === tab.id ? '#ffffff' : '#6b7280',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'providers' && (
          <div className="admin-table-container">
            <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1f2937', margin: 0 }}>Game Providers</h3>
              <button
                onClick={() => {
                  setSelectedProvider(null);
                  setShowProviderModal(true);
                }}
                style={{
                  padding: '8px 16px',
                  background: '#086afb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <FaPlus /> Add Provider
              </button>
            </div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Category</th>
                  <th>Games</th>
                  <th>API Status</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providers.map(provider => (
                  <tr key={provider.id}>
                    <td style={{ fontWeight: 600 }}>{provider.name}</td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {provider.category}
                      </span>
                    </td>
                    <td>{provider.gameCount}</td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        background: '#dcfce7',
                        color: '#166534',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        Connected
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => toggleProvider(provider.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 24,
                          color: provider.enabled ? '#10b981' : '#9ca3af'
                        }}
                      >
                        {provider.enabled ? <FaToggleOn /> : <FaToggleOff />}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => {
                            setSelectedProvider(provider);
                            setShowProviderModal(true);
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#f3f4f6',
                            color: '#1f2937',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 14
                          }}
                        >
                          <FaEdit />
                        </button>
                        <button
                          style={{
                            padding: '6px 12px',
                            background: '#fef2f2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 14
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'games' && (
          <div className="admin-table-container">
            <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1f2937', margin: 0 }}>Games Catalog</h3>
              <button
                onClick={() => {
                  setSelectedGame(null);
                  setShowGameModal(true);
                }}
                style={{
                  padding: '8px 16px',
                  background: '#086afb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <FaPlus /> Add Game
              </button>
            </div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Game Name</th>
                  <th>Provider</th>
                  <th>Category</th>
                  <th>RTP</th>
                  <th>Volatility</th>
                  <th>Bet Limits</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {games.map(game => (
                  <tr key={game.id}>
                    <td style={{ fontWeight: 600 }}>{game.name}</td>
                    <td>{game.provider}</td>
                    <td>{game.category}</td>
                    <td>{game.rtp}</td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        background: game.volatility === 'High' ? '#fef3c7' : '#dcfce7',
                        color: game.volatility === 'High' ? '#92400e' : '#166534',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {game.volatility}
                      </span>
                    </td>
                    <td>€{game.minBet} - €{game.maxBet}</td>
                    <td>
                      <button
                        onClick={() => toggleGame(game.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 24,
                          color: game.enabled ? '#10b981' : '#9ca3af'
                        }}
                      >
                        {game.enabled ? <FaToggleOn /> : <FaToggleOff />}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => {
                            setSelectedGame(game);
                            setShowGameModal(true);
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#f3f4f6',
                            color: '#1f2937',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 14
                          }}
                        >
                          <FaEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'sports' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            <div className="insight-card">
              <h3>
                <FaFutbol style={{ color: '#086afb' }} />
                <span>Odds Feed Configuration</span>
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    Primary Odds Provider
                  </label>
                  <select style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    fontSize: 14
                  }}>
                    <option>BetRadar</option>
                    <option>Oddin</option>
                    <option>Sportradar</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    Update Frequency
                  </label>
                  <select style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    fontSize: 14
                  }}>
                    <option>Real-time (WebSocket)</option>
                    <option>Every 5 seconds</option>
                    <option>Every 30 seconds</option>
                    <option>Every 60 seconds</option>
                  </select>
                </div>
                <button style={{
                  width: '100%',
                  padding: '12px',
                  background: '#086afb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: 8
                }}>
                  Save Configuration
                </button>
              </div>
            </div>

            <div className="insight-card">
              <h3>
                <FaBroadcastTower style={{ color: '#0dca9d' }} />
                <span>Market Management</span>
              </h3>
              <ul className="insights-list">
                <li className="insight-item">
                  <span className="insight-bullet">•</span>
                  <span>Total Active Markets: 1,247</span>
                </li>
                <li className="insight-item">
                  <span className="insight-bullet">•</span>
                  <span>Upcoming Events: 89</span>
                </li>
                <li className="insight-item">
                  <span className="insight-bullet">•</span>
                  <span>Live Events: 12</span>
                </li>
                <li className="insight-item">
                  <span className="insight-bullet">•</span>
                  <span>Total Exposure: €45,230</span>
                </li>
              </ul>
              <button style={{
                width: '100%',
                padding: '12px',
                background: '#f3f4f6',
                color: '#1f2937',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                marginTop: 16
              }}>
                View All Markets
              </button>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 24 }}>
            <div className="insight-card">
              <h3>
                <FaCog style={{ color: '#e7038e' }} />
                <span>General Settings</span>
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Enable iGaming</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Allow retail users to access iGaming features</div>
                  </div>
                  <button style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 32,
                    color: '#10b981'
                  }}>
                    <FaToggleOn />
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Responsible Gaming</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Enable deposit limits and self-exclusion</div>
                  </div>
                  <button style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 32,
                    color: '#10b981'
                  }}>
                    <FaToggleOn />
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Auto-Settle Bets</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Automatically settle bets when results are confirmed</div>
                  </div>
                  <button style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 32,
                    color: '#10b981'
                  }}>
                    <FaToggleOn />
                  </button>
                </div>
              </div>
            </div>

            <div className="insight-card">
              <h3>
                <FaCog style={{ color: '#086afb' }} />
                <span>Risk Management</span>
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    Max Bet Per Game
                  </label>
                  <input
                    type="number"
                    defaultValue="10000"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    Max Exposure Per Event
                  </label>
                  <input
                    type="number"
                    defaultValue="50000"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                  />
                </div>
                <button style={{
                  width: '100%',
                  padding: '12px',
                  background: '#086afb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: 8
                }}>
                  Save Limits
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tetris' && tetrisConfig && editedTetrisConfig && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Statistics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
              <div className="insight-card">
                <h3><FaChartBar style={{ color: '#086afb' }} /><span>Total Sessions</span></h3>
                <p style={{ fontSize: 32, fontWeight: 700, color: '#1f2937', margin: '16px 0 0 0' }}>{tetrisStats.totalSessions}</p>
              </div>
              <div className="insight-card">
                <h3><FaTrophy style={{ color: '#10b981' }} /><span>Win Rate</span></h3>
                <p style={{ fontSize: 32, fontWeight: 700, color: '#10b981', margin: '16px 0 0 0' }}>{tetrisWinRate.toFixed(1)}%</p>
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{tetrisStats.wonSessions}/{tetrisStats.wonSessions + tetrisStats.lostSessions} won</p>
              </div>
              <div className="insight-card">
                <h3><FaCoins style={{ color: '#a855f7' }} /><span>Total Staked</span></h3>
                <p style={{ fontSize: 32, fontWeight: 700, color: '#a855f7', margin: '16px 0 0 0' }}>{formatAmount(tetrisStats.totalStaked, 'EUR')}</p>
              </div>
              <div className="insight-card">
                <h3><FaChartBar style={{ color: tetrisProfit >= 0 ? '#10b981' : '#ef4444' }} /><span>House Profit</span></h3>
                <p style={{ fontSize: 32, fontWeight: 700, color: tetrisProfit >= 0 ? '#10b981' : '#ef4444', margin: '16px 0 0 0' }}>{formatAmount(tetrisProfit, 'EUR')}</p>
              </div>
            </div>

            {/* Game Status */}
            <div className="insight-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                    <FaTrophy style={{ color: '#a855f7' }} />
                    <span>Tetris Game Status</span>
                  </h3>
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0 0' }}>Enable or disable the Tetris game for all users</p>
                </div>
                <button
                  onClick={toggleTetrisGame}
                  disabled={savingTetris}
                  style={{
                    padding: '12px 24px',
                    background: tetrisConfig.isEnabled ? '#10b981' : '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: savingTetris ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: savingTetris ? 0.6 : 1
                  }}
                >
                  {savingTetris ? <FaSpinner className="animate-spin" /> : tetrisConfig.isEnabled ? <FaToggleOn style={{ fontSize: 20 }} /> : <FaToggleOff style={{ fontSize: 20 }} />}
                  {tetrisConfig.isEnabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            </div>

            {/* Configuration */}
            <div className="insight-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h3 style={{ margin: 0 }}>
                  <FaCog style={{ color: '#086afb' }} />
                  <span>Game Configuration</span>
                </h3>
                <button
                  onClick={saveTetrisConfig}
                  disabled={savingTetris}
                  style={{
                    padding: '8px 16px',
                    background: '#086afb',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: savingTetris ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: savingTetris ? 0.6 : 1
                  }}
                >
                  {savingTetris ? <FaSpinner className="animate-spin" /> : <FaSave />}
                  Save Changes
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
                {/* Stake Limits */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h4 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', margin: 0 }}>Stake Limits</h4>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Min Stake (€)</label>
                    <input
                      type="number"
                      value={editedTetrisConfig.minStake}
                      onChange={(e) => setEditedTetrisConfig({ ...editedTetrisConfig, minStake: Number(e.target.value) })}
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Max Stake (€)</label>
                    <input
                      type="number"
                      value={editedTetrisConfig.maxStake}
                      onChange={(e) => setEditedTetrisConfig({ ...editedTetrisConfig, maxStake: Number(e.target.value) })}
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                    />
                  </div>
                </div>

                {/* Difficulty Settings */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h4 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', margin: 0 }}>Difficulty Settings</h4>
                  
                  {/* Easy */}
                  <div style={{ padding: 12, background: '#dcfce7', borderRadius: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#166534', marginBottom: 8 }}>Easy Mode</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>Target Score</label>
                        <input
                          type="number"
                          value={editedTetrisConfig.easyTargetScore}
                          onChange={(e) => setEditedTetrisConfig({ ...editedTetrisConfig, easyTargetScore: Number(e.target.value) })}
                          style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>Multiplier</label>
                        <input
                          type="number"
                          step="0.1"
                          value={editedTetrisConfig.easyMultiplier}
                          onChange={(e) => setEditedTetrisConfig({ ...editedTetrisConfig, easyMultiplier: Number(e.target.value) })}
                          style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Medium */}
                  <div style={{ padding: 12, background: '#fef3c7', borderRadius: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>Medium Mode</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>Target Score</label>
                        <input
                          type="number"
                          value={editedTetrisConfig.mediumTargetScore}
                          onChange={(e) => setEditedTetrisConfig({ ...editedTetrisConfig, mediumTargetScore: Number(e.target.value) })}
                          style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>Multiplier</label>
                        <input
                          type="number"
                          step="0.1"
                          value={editedTetrisConfig.mediumMultiplier}
                          onChange={(e) => setEditedTetrisConfig({ ...editedTetrisConfig, mediumMultiplier: Number(e.target.value) })}
                          style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Hard */}
                  <div style={{ padding: 12, background: '#fecaca', borderRadius: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#991b1b', marginBottom: 8 }}>Hard Mode</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>Target Score</label>
                        <input
                          type="number"
                          value={editedTetrisConfig.hardTargetScore}
                          onChange={(e) => setEditedTetrisConfig({ ...editedTetrisConfig, hardTargetScore: Number(e.target.value) })}
                          style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>Multiplier</label>
                        <input
                          type="number"
                          step="0.1"
                          value={editedTetrisConfig.hardMultiplier}
                          onChange={(e) => setEditedTetrisConfig({ ...editedTetrisConfig, hardMultiplier: Number(e.target.value) })}
                          style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Sessions */}
            <div className="admin-table-container">
              <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1f2937', margin: 0 }}>Recent Game Sessions</h3>
              </div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Difficulty</th>
                    <th>Stake</th>
                    <th>Score</th>
                    <th>Payout</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {tetrisSessions.slice(0, 20).map((session) => (
                    <tr key={session.id}>
                      <td style={{ fontWeight: 600 }}>{session.username}</td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          background: session.difficulty === 'EASY' ? '#dcfce7' : session.difficulty === 'MEDIUM' ? '#fef3c7' : '#fecaca',
                          color: session.difficulty === 'EASY' ? '#166534' : session.difficulty === 'MEDIUM' ? '#92400e' : '#991b1b',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600
                        }}>
                          {session.difficulty}
                        </span>
                      </td>
                      <td>{formatAmount(session.stake, 'EUR')}</td>
                      <td>{session.actualScore !== null ? `${session.actualScore}/${session.targetScore}` : '-'}</td>
                      <td style={{ fontWeight: 600, color: session.status === 'WON' ? '#10b981' : '#6b7280' }}>
                        {session.status === 'WON' ? formatAmount(session.actualWin, 'EUR') : '-'}
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          background: session.status === 'WON' ? '#dcfce7' : session.status === 'LOST' ? '#fecaca' : '#e0f2fe',
                          color: session.status === 'WON' ? '#166534' : session.status === 'LOST' ? '#991b1b' : '#0369a1',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600
                        }}>
                          {session.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{new Date(session.startedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tetrisSessions.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                  No game sessions yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IGamingManagement;
